import React from 'react'
import i18n from '../i18n'
import styled from 'styled-components'
import { X } from 'lucide-react'

// Overlay per il modal
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  padding: 1rem;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`

// Modal box
const ModalBox = styled.div`
  background: ${(props) => props.theme.colors.background.primary};
  border-radius: 12px;
  padding: 2rem;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  transform: ${({ $visible }) => ($visible ? 'scale(1)' : 'scale(0.95)')};
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 1.5rem;
  }
`

// Close button
const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.text.secondary};
  opacity: 0.6;
  transition: opacity 0.2s ease;
  border-radius: 6px;

  &:hover {
    opacity: 1;
    background: ${(props) => props.theme.colors.background.secondary};
  }
`

// Title
const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.primary};
  text-align: center;
  margin: 0;
  letter-spacing: -0.02em;
`

// Subtitle
const SubTitle = styled.p`
  text-align: center;
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: 0.875rem;
  margin: -0.5rem 0 0 0;
`

// Options container
const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

// Language option
const LanguageOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 1rem 1.25rem;
  border: 2px solid ${(props) => props.theme.colors.border.medium};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => props.theme.colors.background.primary};
  width: 100%;
  gap: 1rem;

  &:hover {
    border-color: ${(props) => props.theme.colors.text.primary};
    background: ${(props) => props.theme.colors.background.secondary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
  }
`

// Flag icon
const FlagIcon = styled.span`
  font-size: 2rem;
  line-height: 1;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`

// Language info container
const LanguageInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`

// Language name
const LanguageName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.primary};
  margin-bottom: 0.125rem;
`

// Language code/description
const LanguageCode = styled.span`
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.8;
`

// Opzioni di lingua disponibili
const languageOptions = [
  {
    code: 'it',
    name: 'Italiano',
    flag: '🇮🇹',
    description: 'Italian'
  },
  {
    code: 'en',
    name: 'English',
    flag: '🇬🇧',
    description: 'English'
  },
  {
    code: 'fr',
    name: 'Français',
    flag: '🇫🇷',
    description: 'French'
  }
]

/**
 * Componente Dialog per la selezione della lingua del PDF
 * Modernizzato per coerenza con il design system
 *
 * @param {boolean} visible - Controlla la visibilità del dialog
 * @param {function} onLanguageSelect - Callback chiamata quando viene selezionata una lingua
 * @param {function} onClose - Callback chiamata quando viene chiuso il dialog
 * @param {string} documentType - Tipo di documento (fattura, ordine, etc.) per personalizzare il titolo
 */
const LanguageSelectDialog = ({
  visible,
  onLanguageSelect,
  onClose,
  documentType = 'documento'
}) => {
  const handleLanguageSelect = (languageCode) => {
    if (onLanguageSelect) {
      onLanguageSelect(languageCode)
    }
    if (onClose) {
      onClose()
    }
  }

  const handleOverlayClick = (e) => {
    // Chiudi il dialog solo se si clicca sull'overlay (non sul contenuto)
    if (e.target === e.currentTarget && onClose) {
      onClose()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && onClose) {
      onClose()
    }
  }

  React.useEffect(() => {
    if (visible) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, onClose])

  if (!visible) return null

  return (
    <Overlay $visible={visible} onClick={handleOverlayClick}>
      <ModalBox $visible={visible} onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Chiudi">
          <X size={20} />
        </CloseButton>

        <Title>{i18n.t('ui.selezionaLingua', { ns: 'common' })}</Title>

        <SubTitle>{i18n.t('ui.scegliLinguaPdf', { ns: 'common', documentType })}</SubTitle>

        <OptionsContainer>
          {languageOptions.map((language) => (
            <LanguageOption
              key={language.code}
              onClick={() => handleLanguageSelect(language.code)}
              aria-label={`Seleziona ${language.name}`}
            >
              <FlagIcon aria-hidden="true">{language.flag}</FlagIcon>
              <LanguageInfo>
                <LanguageName>{language.name}</LanguageName>
                <LanguageCode>{language.description}</LanguageCode>
              </LanguageInfo>
            </LanguageOption>
          ))}
        </OptionsContainer>
      </ModalBox>
    </Overlay>
  )
}

export default LanguageSelectDialog
